/* eslint-disable max-lines */
"use client";
import { clearCartConstants, CAKE_TYPE } from "../../../constants/pageConstants/pdpConstants";
import PopOver from "../../atoms/popOver";
import PropTypes from "prop-types";
import Typography from "../../atoms/typography";
import { XCircleIcon, WarningIcon, ArrowLeftIcon } from "@phosphor-icons/react";
import ButtonComponent from "../../atoms/button";
import Drawer from "../../molecules/drawer";
import Portal from "../../molecules/Portal";
import {
  setIsShowCartEmpty,
  setSelectedDomesticLocation,
  setSelectedDomesticLocationForCart,
  setSelectedIntLocation,
  setSelectedIntLocationForCart,
  setUnserviceableCartItems,
  setIsClearCartDueToCountryChange,
  setPendingCountryChange,
  setSelectedCountryAction,
  setShouldClearLocationLockInput,
  setSkipCartValidationOnce,
  setShouldRetryAddToCartAfterClear,
  setIsShowMobileLocationLockAction,
  setIsShowMobileLocationLockDrawerAction,
  setIsShowDesktopLocationLockAction,
} from "../../../store/slices/locationLock";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { useCallback, useEffect, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import { initiateClearCartApiCall } from "../../../api/common";
import { initiateFus } from "../../../store/slices/common/fus";
import { responseGetCartItemsCount, setCartItems } from "../../../store/slices/cartSlice";
import CookieUtil from "../../../utils/storage/cookieUtil";
import {
  INTERNATIONAL_PF_COOKIE,
  LAST_VISITED_ADDRESS,
  LAST_VISITED_INTERNATIONAL_CITY,
  LAST_VISITED_INTERNATIONAL_LOCATION,
  LAST_VISITED_PINCODE,
  PF_COOKIE,
  SELECTED_INTERNATIONAL_CITY,
} from "../../../constants/storage";
import { getDeliveryAreasAsKey } from "../../../api/locationLock";
import ItemCard from "../itemCard";
import {
  calDiscountPercentage,
  formatClassNames,
  getProductDeliveryType,
  isJsonString,
  pageName,
} from "../../../utils/common";
import { calenderIsTodayOrTomorrowDate } from "../../../utils/common/deliveryDate";
import isEmpty from "lodash.isempty";
import useAnalytics from "../../../analytics/useAnalytics";
import { cleverTapCommonEvents } from "../../../analytics/clevertapConstants";
import { buildClearCartPopupViewedData } from "../../../utils/clevertapCommon";
import { checkMultiUploadedImageCount } from "../../../utils/pdp/personalization";
import { COUNTRIES } from "../../../constants/common/common";
import { armSkipCartValidationOnce } from "../../../utils/locationLock/validateCartServiceabilityOnLocationChange";

/** Matches Drawer close animation so ClearCart can unmount after slide-out without changing Drawer. */
const CLEAR_CART_DRAWER_ANIMATION_MS = 300;

const cartLineItemPropType = PropTypes.shape({
  orderItemSeqId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  image: PropTypes.string,
  title: PropTypes.string,
  price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  qty: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
});

const cartItemPropType = PropTypes.shape({
  orderItemSeqId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  image: PropTypes.string,
  title: PropTypes.string,
  price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  qty: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  addons: PropTypes.arrayOf(cartLineItemPropType),
});

/**
 * Renders a cart item with its addons.
 *
 * @param {Object} props - Component props
 * @param {Object} props.item - Cart item details
 * @param {boolean} props.isMobile - True if viewed on mobile
 * @param {string} props.selectedCurrency - Active currency code
 * @returns {JSX.Element} CartItem component
 */
const CartItem = ({ item, isMobile, selectedCurrency }) => {
  return (
    <div className="flex w-full flex-col gap-[16px] p-16">
      <ItemCard item={item} isMobile={isMobile} selectedCurrency={selectedCurrency} />

      {item.addons?.length > 0 && <div className="w-full border-t border-dashed border-[#EDEDED]" />}

      {item.addons?.map((addon, index) => (
        <ItemCard
          key={`${addon.orderItemSeqId}-${index}`}
          item={addon}
          isMobile={isMobile}
          selectedCurrency={selectedCurrency}
          isAddon={true}
        />
      ))}
    </div>
  );
};

CartItem.propTypes = {
  item: cartItemPropType.isRequired,
  isMobile: PropTypes.bool.isRequired,
  selectedCurrency: PropTypes.object,
};

/**
 * Renders the header of the clear cart modal.
 *
 * @param {Object} props - Component props
 * @param {boolean} props.isMobile - True if viewed on mobile
 * @param {Function} props.onClose - Function to close the modal
 * @returns {JSX.Element} ClearCartHeader component
 */
const ClearCartHeader = ({ isMobile, onClose }) => (
  <div className="flex items-center justify-start gap-16 border-b border-lightGrey p-16 md:justify-between">
    {isMobile && (
      <button onClick={onClose}>
        <ArrowLeftIcon size={28} />
      </button>
    )}

    <Typography variant="p" Tag="p" classes="font-600 text-16 md:text-20 text-fnp-500">
      {clearCartConstants.TITLE}
    </Typography>

    {!isMobile && (
      <button onClick={onClose}>
        <XCircleIcon size={24} />
      </button>
    )}
  </div>
);

ClearCartHeader.propTypes = {
  isMobile: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

/**
 * Renders the warning message of the clear cart modal.
 *
 * @param {Object} props
 * @param {string} [props.selectedLocationLabel] - Selected pincode or international city.
 * @param {boolean} [props.isDomestic] - Whether the pending location is a domestic pincode.
 * @param {boolean} [props.isCountryChange] - Whether ClearCart opened due to country change.
 * @returns {JSX.Element} ClearCartWarning component
 */
const ClearCartWarning = ({ selectedLocationLabel = "", isDomestic = true, isCountryChange = false }) => {
  if (isCountryChange) {
    return (
      <div className="flex w-full justify-between gap-8 rounded-[8px] bg-[#D6492714] p-10">
        <WarningIcon size={20} weight="fill" className="flex-none text-red-100" />

        <Typography variant="p" Tag="p" classes="font-400 text-12 text-red-100 leading-[16px] flex-1">
          {clearCartConstants.WARNING_TEXT_COUNTRY_CHANGE}
        </Typography>
      </div>
    );
  }

  const locationSuffix = selectedLocationLabel
    ? isDomestic
      ? `${selectedLocationLabel} pincode`
      : selectedLocationLabel
    : "";

  return (
    <div className="flex w-full justify-between gap-8 rounded-[8px] bg-[#D6492714] p-10">
      <WarningIcon size={20} weight="fill" className="flex-none text-red-100" />

      <Typography variant="p" Tag="p" classes="font-400 text-12 text-red-100 leading-[16px] flex-1">
        {clearCartConstants.WARNING_TEXT}
        {locationSuffix ? <span className="font-600">{locationSuffix}</span> : null}
      </Typography>
    </div>
  );
};

ClearCartWarning.propTypes = {
  selectedLocationLabel: PropTypes.string,
  isDomestic: PropTypes.bool,
  isCountryChange: PropTypes.bool,
};

/**
 * Renders the list of cart items.
 *
 * @param {Object} props - Component props
 * @param {Object} props.items - List of cart items
 * @param {boolean} props.isMobile - True if viewed on mobile
 * @param {string} props.selectedCurrency - Active currency code
 * @param {boolean} [props.isCountryChange] - Whether ClearCart opened due to country change.
 * @returns {JSX.Element} CartItemsList component
 */
const CartItemsList = ({ items = [], isMobile, selectedCurrency, isCountryChange = false }) => (
  <div
    className={formatClassNames(
      "flex flex-col gap-8 rounded-[8px]",
      isCountryChange
        ? "border border-thinLightGrey"
        : "border border-red bg-[linear-gradient(180deg,#FEF2F2_0%,#FFFFFF_40%)]",
    )}
  >
    {!isCountryChange && (
      <div className="md:roundet-t-8 flex w-full items-center justify-between gap-8 px-16 py-8">
        <WarningIcon size={20} weight="fill" className="flex-none text-red-100" />
        <Typography variant="p" Tag="p" classes="font-600 text-12 text-red-100 leading-[16px] flex-1">
          Out of Stock
        </Typography>
      </div>
    )}
    {items?.map((item, index) => {
      return (
        <div key={item.orderItemSeqId}>
          <CartItem item={item?.giftItem} isMobile={isMobile} selectedCurrency={selectedCurrency} />

          {index !== items.length - 1 && <div className="h-[1px] w-full bg-[#EDEDED] md:my-8" />}
        </div>
      );
    })}
  </div>
);

CartItemsList.propTypes = {
  items: PropTypes.arrayOf(cartItemPropType).isRequired,
  isMobile: PropTypes.bool.isRequired,
  selectedCurrency: PropTypes.object,
  isCountryChange: PropTypes.bool,
};

/**
 * Renders the footer of the clear cart modal.
 *
 * @param {Object} props - Component props
 * @param {Function} props.onChangeLocation - Applies pending location cookies and continues flow
 * @param {Function} props.onViewCart - Function to navigate to cart
 * @param {Function} [props.onChangeCountry] - Clears cart then redirects for country change
 * @param {boolean} [props.isChangeCountry] - True when cart items are from a different catalog/country.
 * @returns {JSX.Element} ClearCartFooter component
 */
const ClearCartFooter = ({ onChangeLocation, onViewCart, onChangeCountry, isChangeCountry = false }) => {
  const onHandleOutlinedClick = isChangeCountry ? onChangeCountry : onChangeLocation;

  return (
    <div className="flex w-full gap-[16px] border-t border-lightGrey p-16">
      <ButtonComponent
        buttonVariant="outlined"
        buttonTextSize="large"
        classes="text-base flex justify-center w-full"
        label={isChangeCountry ? clearCartConstants.CHANGE_COUNTRY : clearCartConstants.CHANGE_LOCATION}
        onClick={onHandleOutlinedClick}
        isRemoveExtraPadding={true}
      />

      <ButtonComponent
        buttonVariant="filled"
        buttonTextSize="large"
        classes="text-base flex justify-center w-full"
        label={clearCartConstants.VIEW_CART}
        onClick={onViewCart}
        isRemoveExtraPadding={true}
      />
    </div>
  );
};

ClearCartFooter.propTypes = {
  onChangeLocation: PropTypes.func.isRequired,
  onViewCart: PropTypes.func.isRequired,
  onChangeCountry: PropTypes.func,
  isChangeCountry: PropTypes.bool,
};

/**
 * Renders the main component of the clear cart modal.
 *
 * @param {Object} props - Component props
 * @param {boolean} props.isMobile - True if viewed on mobile
 * @param {Object} props.selectedCurrency - Active currency object
 * @returns {JSX.Element} ClearCart component
 */
const ClearCart = ({ isMobile, selectedCurrency }) => {
  const dispatch = useDispatch();
  const pathname = usePathname();
  const { trackEvent } = useAnalytics();
  const {
    cartItems,
    cartCount,
    unserviceableCartItems,
    isCountryChange,
    pendingCountryChange,
    shouldRetryAddToCartAfterClear,
    selectedDomesticLocationForCart,
    selectedIntLocationForCart,
    catalogId,
    visitedCountry,
    currentItem,
    clientVariantType,
    selectedDate,
    deliveryDetailsData,
    deliveryDates,
    nextAvailableDifference,
    isPayLaterVisible,
    uploadFinalStatus,
    multiImageConfig,
    countryListData,
  } = useSelector(
    (state) => ({
      cartItems: state.cart.cartItems,
      cartCount: state.cart.cartCount,
      unserviceableCartItems: state.locationLock.unserviceableCartItems,
      isCountryChange: state.locationLock.isClearCartDueToCountryChange,
      pendingCountryChange: state.locationLock.pendingCountryChange,
      shouldRetryAddToCartAfterClear: state.locationLock.shouldRetryAddToCartAfterClear,
      selectedDomesticLocationForCart: state.locationLock.selectedDomesticLocationForCart,
      selectedIntLocationForCart: state.locationLock.selectedIntLocationForCart,
      catalogId: state.appConfig.catalogId,
      visitedCountry: state.locationLock.visitedCountry,
      currentItem: state?.pdp?.clientCurrentClickedItem,
      clientVariantType: state?.pdp?.clientVariantType,
      selectedDate: state?.pdp?.selectedDate,
      deliveryDetailsData: state?.shippingDateAndSlotSelection?.selectedDeliveryDetails,
      deliveryDates: state?.shippingDateAndSlotSelection?.deliveryDates,
      nextAvailableDifference: state?.shippingDateAndSlotSelection?.nextAvailableDifference,
      isPayLaterVisible: state?.pdp?.isPayLaterVisible,
      uploadFinalStatus: state?.personalization?.uploadFinalStatus,
      multiImageConfig: state?.personalization?.multiImageConfig,
      countryListData: state?.locationLock?.countryListData,
    }),
    shallowEqual,
  );
  const urlIdentifier = currentItem?.urlIdentifier;
  const isDomesticLocation = Boolean(selectedDomesticLocationForCart?.pincode);
  const selectedLocationLabel = isDomesticLocation
    ? selectedDomesticLocationForCart.pincode
    : selectedIntLocationForCart?.address || selectedIntLocationForCart?.pincode || "";
  const itemsToShow = isCountryChange
    ? cartItems
    : unserviceableCartItems?.length > 0
      ? unserviceableCartItems
      : cartItems;

  // Start closed so Drawer can paint translateY(100%), then open for bottom→top slide-in.
  // Close sets this false first so top→bottom plays before Redux unmounts ClearCart.
  const [isDrawerOpen, setIsDrawerOpen] = useState(!isMobile);
  const closeAnimationTimerRef = useRef(null);
  const hasTrackedPopupViewRef = useRef(false);
  const hasTrackedPopupClosedRef = useRef(false);
  const countryId = visitedCountry?.countryId;

  const getClearCartPopupCleverTapData = useCallback(() => {
    const isHome = pathname === "/";
    const isPLP = pathname?.includes("-lp");
    const firstGiftItem = itemsToShow?.[0]?.giftItem;
    const oldPincode = firstGiftItem?.mainproduct?.pincode || firstGiftItem?.pincode || "";
    const newPincode =
      selectedDomesticLocationForCart?.pincode ||
      selectedIntLocationForCart?.pincode ||
      pendingCountryChange?.locationDetails?.pincode ||
      "";
    const cartCatalogId = firstGiftItem?.productCatalogId || firstGiftItem?.catalogId || "";
    const oldCountry = cartCatalogId === COUNTRIES.INDIA ? "India" : visitedCountry?.countryName || cartCatalogId || "";
    // new_country only when ClearCart opened due to a country/catalog change.
    const newCountry = isCountryChange
      ? pendingCountryChange?.selectedCountry?.countryName ||
        (catalogId === COUNTRIES.INDIA ? "India" : catalogId) ||
        ""
      : "";

    return buildClearCartPopupViewedData({
      page_name: pageName(isHome, isPLP, pathname, urlIdentifier),
      page_type: (typeof window !== "undefined" && window.fnpPageType) || "",
      cartItems: itemsToShow,
      new_pincode: newPincode,
      old_pincode: oldPincode,
      new_country: newCountry,
      old_country: oldCountry,
    });
  }, [
    pathname,
    itemsToShow,
    selectedDomesticLocationForCart?.pincode,
    selectedIntLocationForCart?.pincode,
    pendingCountryChange,
    isCountryChange,
    catalogId,
    visitedCountry?.countryName,
    urlIdentifier,
  ]);

  const trackClearCartPopupClosed = useCallback(() => {
    if (hasTrackedPopupClosedRef.current || !itemsToShow?.length) {
      return;
    }
    hasTrackedPopupClosedRef.current = true;
    trackEvent(cleverTapCommonEvents.clearCartPopupClosed, getClearCartPopupCleverTapData(), ["clevertap"]);
  }, [getClearCartPopupCleverTapData, itemsToShow?.length, trackEvent]);

  const trackClearCartClicked = useCallback(
    (cta) => {
      trackEvent(
        cleverTapCommonEvents.clearCartClicked,
        {
          ...getClearCartPopupCleverTapData(),
          cta,
        },
        ["clevertap"],
      );
    },
    [getClearCartPopupCleverTapData, trackEvent],
  );

  useEffect(() => {
    if (!isMobile) {
      return undefined;
    }
    let openFrame = requestAnimationFrame(() => {
      openFrame = requestAnimationFrame(() => {
        setIsDrawerOpen(true);
      });
    });
    return () => {
      cancelAnimationFrame(openFrame);
      if (closeAnimationTimerRef.current) {
        clearTimeout(closeAnimationTimerRef.current);
      }
    };
  }, [isMobile]);

  useEffect(() => {
    if (hasTrackedPopupViewRef.current || !itemsToShow?.length) {
      return;
    }
    hasTrackedPopupViewRef.current = true;
    trackEvent(cleverTapCommonEvents.clearCartPopupViewed, getClearCartPopupCleverTapData(), ["clevertap"]);
    // Track once per ClearCart mount when items are available.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemsToShow?.length]);

  /**
   * Runs mobile drawer close animation, then executes cleanup (Redux unmount).
   * Desktop skips animation and runs cleanup immediately.
   *
   * @param {Function} [onClosed] - Called after close animation (or immediately on desktop).
   * @returns {void}
   */
  const closeClearCartWithAnimation = useCallback(
    (onClosed) => {
      if (!isMobile) {
        onClosed?.();
        return;
      }
      setIsDrawerOpen(false);
      if (closeAnimationTimerRef.current) {
        clearTimeout(closeAnimationTimerRef.current);
      }
      closeAnimationTimerRef.current = setTimeout(() => {
        closeAnimationTimerRef.current = null;
        onClosed?.();
      }, CLEAR_CART_DRAWER_ANIMATION_MS);
    },
    [isMobile],
  );

  /** Navigates to the checkout page. */
  const onHandleViewCart = () => {
    trackClearCartClicked(clearCartConstants.VIEW_CART);

    const isHome = pathname === "/";
    const isPLP = pathname?.includes("-lp");
    const firstGiftItem = itemsToShow?.[0]?.giftItem;
    const mainProduct = firstGiftItem?.mainproduct;
    const isInternational =
      (currentItem?.catalogId || firstGiftItem?.productCatalogId || catalogId) !== COUNTRIES.INDIA;
    const dateOfDelivery = deliveryDetailsData?.selectedDate || selectedDate || mainProduct?.deliveryDate || "";
    const selectedDateFlags = calenderIsTodayOrTomorrowDate(dateOfDelivery) || {};
    const availableDates = deliveryDates?.deliveryDates || [];
    const productImage =
      currentItem?.productContent?.productExtraLargeImages?.[0] ||
      currentItem?.productContent?.productImages?.[0] ||
      mainProduct?.productImage ||
      "";
    const listPrice = currentItem?.productPrice?.listPrice ?? mainProduct?.listPrice;
    const price = currentItem?.productPrice?.price ?? mainProduct?.price;

    trackEvent(
      cleverTapCommonEvents.viewCartClicked,
      {
        product_count: cartCount || itemsToShow?.length || 0,
        is_date_today: Boolean(selectedDateFlags.isDateToday),
        is_date_tomorrow: Boolean(selectedDateFlags.isDateTomorrow),
        is_today_available: availableDates.some((date) => calenderIsTodayOrTomorrowDate(date)?.isDateToday),
        is_tomorrow_available: availableDates.some((date) => calenderIsTodayOrTomorrowDate(date)?.isDateTomorrow),
        next_available_date_difference: isInternational ? "" : (nextAvailableDifference?.nextAvailableDateDiff ?? ""),
        next_available_time_difference: isInternational
          ? ""
          : (nextAvailableDifference?.nextAvailableTimeDiff ??
            nextAvailableDifference?.nextAvailableTimeDifference ??
            ""),
        page_type: (typeof window !== "undefined" && window.fnpPageType) || "",
        page_name: pageName(
          isHome,
          isPLP,
          pathname,
          urlIdentifier || currentItem?.productName || mainProduct?.productName,
        ),
        product_name: currentItem?.productName || mainProduct?.productName || "",
        pid: currentItem?.productId || mainProduct?.productId || "",
        product_category: currentItem?.primaryCategoryId || mainProduct?.primaryProductCategoryId || "",
        is_variant_available: currentItem?.isVariant === "Y" || mainProduct?.isVariant === "Y",
        is_egg: clientVariantType?.toUpperCase() === CAKE_TYPE.WITH_EGG,
        delivery_type: getProductDeliveryType(currentItem?.primaryCategoryId || mainProduct?.primaryProductCategoryId),
        product_price: price,
        currency: selectedCurrency?.currency?.id ?? "INR",
        strike_price: listPrice,
        discount_percentage: calDiscountPercentage(listPrice, price),
        is_product_available: Boolean(currentItem?.stockAvailable ?? true),
        is_international: isInternational,
        city: isInternational ? selectedIntLocationForCart?.address || firstGiftItem?.deliveryLocation || "" : "",
        area: isInternational ? "" : mainProduct?.pincode ? firstGiftItem?.deliveryLocation || "" : "",
        pincode: isInternational ? "" : mainProduct?.pincode || "",
        system_pincode: isInternational ? "" : mainProduct?.pincode || "",
        country: catalogId,
        photo_count: checkMultiUploadedImageCount(multiImageConfig) || 0,
        is_photo_uploaded: Boolean(uploadFinalStatus),
        is_variant_selected: Boolean(urlIdentifier && !pathname?.includes(urlIdentifier)),
        product_rating: currentItem?.productReviewsAndRatings?.ratingValue || mainProduct?.ratingValue || "",
        product_review_count: currentItem?.productReviewsAndRatings?.totalReview || mainProduct?.totalReview || "",
        earliest_delivery_date: availableDates?.[0] || "",
        complete_url: typeof window !== "undefined" ? window.location.href : "",
        clean_url: pathname,
        date_of_delivery: dateOfDelivery,
        sku_id: currentItem?.skuCode || mainProduct?.sku || mainProduct?.skuCode || "",
        image_url: productImage
          ? productImage.startsWith("http")
            ? productImage
            : `${process.env.NEXT_PUBLIC_ASSET_PREFIX}${productImage}`
          : "",
        attribution_url: typeof document !== "undefined" ? document?.referrer || "" : "",
        is_payLater_visible: Boolean(isPayLaterVisible),
        flow: "new",
      },
      ["clevertap"],
    );

    window.location.href = "/checkout/delivery-address";
  };

  /**
   * Retrieves the default selected country from the country list based on the catalog ID.
   * If no catalog ID is found in the visited country, the function defaults to the provided catalog ID.
   *
   * @returns {Object | undefined} The default selected country or undefined if no match is found.
   * @date 08/11/2024 - 14:00:00
   */
  const updateLocationLockData = () => {
    if (Array.isArray(countryListData)) {
      const defaultSelectedCountry = countryListData?.filter((country) => {
        const getVerifycatelogID = isEmpty(visitedCountry?.catelogID) ? catalogId : visitedCountry?.catelogID;

        return country.catelogID === getVerifycatelogID;
      });

      if (defaultSelectedCountry?.length > 0) {
        return defaultSelectedCountry?.[0];
      }
    }
  };

  /**
   * Applies pending domestic/international location cookies from ClearCart state.
   * @returns {Promise<void>}
   */
  const applyPendingLocationCookies = async () => {
    const { pincode, address } = selectedDomesticLocationForCart || {};
    const { pincode: intPincode, address: intAddress } = selectedIntLocationForCart || {};

    if (pincode && address) {
      const cookieOptions = {
        maxAge: 60 * 24 * 60 * 30,
        path: "/",
        encode: decodeURIComponent,
      };
      CookieUtil.setCookie(LAST_VISITED_PINCODE, pincode, cookieOptions);
      CookieUtil.setCookie(LAST_VISITED_ADDRESS, address, cookieOptions);
      const deliveryAreasResponse = await getDeliveryAreasAsKey(pincode);
      if (deliveryAreasResponse?.isSuccess && deliveryAreasResponse?.cacheKey) {
        CookieUtil.setCookie(PF_COOKIE, deliveryAreasResponse.cacheKey, cookieOptions);
      }
      dispatch(setSelectedDomesticLocation({ pincode, address }));
      return;
    }

    if (intPincode && intAddress) {
      const intCookieOptions = { maxAge: 60 * 60 * 24 * 7, path: "/", encode: decodeURIComponent };
      const deliveryAreasResponse = await getDeliveryAreasAsKey(intPincode);
      if (deliveryAreasResponse?.isSuccess && deliveryAreasResponse?.cacheKey) {
        CookieUtil.setCookie(INTERNATIONAL_PF_COOKIE, deliveryAreasResponse.cacheKey, intCookieOptions);
      }
      CookieUtil.setCookie(LAST_VISITED_INTERNATIONAL_LOCATION, intAddress, intCookieOptions);
      CookieUtil.setCookie(LAST_VISITED_INTERNATIONAL_CITY, intPincode, intCookieOptions);

      const selectedIntlCity = CookieUtil.getCookie(SELECTED_INTERNATIONAL_CITY);
      const country = updateLocationLockData(intPincode);

      const selectedCountriesInCookie =
        selectedIntlCity && isJsonString(selectedIntlCity) ? JSON.parse(selectedIntlCity) : {};
      const data = {
        ...selectedCountriesInCookie,
        [countryId]: {
          country: country.countryName,
          categoryId: `${country?.catelogID}/gifts`,
          catalogId: country?.catelogID,
          countryId: country?.countryId,
          countryName: country.countryName,
          city: intAddress,
          cityId: intPincode,
        },
      };
      CookieUtil.setCookie(SELECTED_INTERNATIONAL_CITY, JSON.stringify(data), intCookieOptions);
      dispatch(setSelectedIntLocation({ pincode: intPincode, location: intAddress }));
    }
  };

  /**
   * Applies pending country cookies/location, then redirects — unless the caller that
   * parked the change suppressed navigation, in which case it is told the commit landed
   * and owns what happens next.
   * @returns {Promise<void>}
   */
  const applyPendingCountryAndRedirect = async () => {
    const { redirectUrl, locationDetails, selectedCountry, countryCookieKey, isInternationalTarget } =
      pendingCountryChange || {};
    const { pincode, location } = locationDetails || {};

    if (!isEmpty(selectedCountry)) {
      dispatch(setSelectedCountryAction(selectedCountry));
    }

    if (pincode && location) {
      const deliveryAreasResponse = await getDeliveryAreasAsKey(pincode);

      if (isInternationalTarget) {
        const intCookieOptions = { maxAge: 60 * 60 * 24 * 7, path: "/", encode: decodeURIComponent };
        if (deliveryAreasResponse?.isSuccess && deliveryAreasResponse?.cacheKey) {
          CookieUtil.setCookie(INTERNATIONAL_PF_COOKIE, deliveryAreasResponse.cacheKey, intCookieOptions);
        }
        CookieUtil.setCookie(LAST_VISITED_INTERNATIONAL_LOCATION, location, intCookieOptions);
        CookieUtil.setCookie(LAST_VISITED_INTERNATIONAL_CITY, pincode, intCookieOptions);

        const selectedIntlCity = CookieUtil.getCookie(SELECTED_INTERNATIONAL_CITY);
        const selectedCountriesInCookie =
          selectedIntlCity && isJsonString(selectedIntlCity) ? JSON.parse(selectedIntlCity) : {};
        const cookieKey = countryCookieKey || selectedCountry?.countryId;
        if (cookieKey) {
          const data = {
            ...selectedCountriesInCookie,
            [cookieKey]: {
              country: selectedCountry?.countryName,
              categoryId: `${selectedCountry?.catelogID}/gifts`,
              catalogId: selectedCountry?.catelogID,
              countryId: selectedCountry?.countryId,
              countryName: selectedCountry?.countryName,
              city: location,
              cityId: pincode,
            },
          };
          CookieUtil.setCookie(SELECTED_INTERNATIONAL_CITY, JSON.stringify(data), intCookieOptions);
        }
        dispatch(setSelectedIntLocation({ pincode, location }));
      } else {
        const cookieOptions = {
          maxAge: 60 * 24 * 60 * 30,
          path: "/",
          encode: decodeURIComponent,
        };
        if (deliveryAreasResponse?.isSuccess && deliveryAreasResponse?.cacheKey) {
          CookieUtil.setCookie(PF_COOKIE, deliveryAreasResponse.cacheKey, cookieOptions);
        }
        CookieUtil.setCookie(LAST_VISITED_PINCODE, pincode, cookieOptions);
        CookieUtil.setCookie(LAST_VISITED_ADDRESS, location, cookieOptions);
        dispatch(setSelectedDomesticLocation({ pincode, address: location }));
      }
    }

    window.location.href = redirectUrl || "/";
  };

  /**
   * Clears ClearCart Redux flags so the parent can unmount this component.
   * @param {Object} [options]
   * @param {boolean} [options.clearLocationInput=false] - Clear pincode/city input (back/dismiss).
   * @returns {void}
   */
  const resetClearCartState = ({ clearLocationInput = false } = {}) => {
    dispatch(setIsShowCartEmpty(false));
    dispatch(setIsClearCartDueToCountryChange(false));
    dispatch(setPendingCountryChange(null));
    dispatch(setShouldRetryAddToCartAfterClear(false));
    dispatch(setUnserviceableCartItems([]));
    dispatch(setSelectedDomesticLocationForCart({}));
    dispatch(setSelectedIntLocationForCart({}));
    if (clearLocationInput) {
      dispatch(setShouldClearLocationLockInput(true));
    }
  };

  /**
   * Change Country:
   * - Country dropdown switch: clear cart, apply pending country, redirect.
   * - Cross-catalog on current page (manual URL / city / ATC): clear cart, apply
   *   selected city/pincode, then retry ATC/Buy Now or close location lock.
   * @returns {Promise<void>}
   */
  const onHandleChangeCountry = async () => {
    trackClearCartClicked(clearCartConstants.CHANGE_COUNTRY);
    const response = await initiateClearCartApiCall();
    if (response?.responseStatus !== "success") {
      return;
    }

    dispatch(responseGetCartItemsCount(0));
    dispatch(setCartItems([]));
    dispatch(initiateFus());
    dispatch(setUnserviceableCartItems([]));

    // Explicit country-list switch → redirect to the selected country.
    if (pendingCountryChange) {
      closeClearCartWithAnimation(async () => {
        dispatch(setIsShowCartEmpty(false));
        dispatch(setIsClearCartDueToCountryChange(false));
        dispatch(setShouldRetryAddToCartAfterClear(false));
        await applyPendingCountryAndRedirect();
        dispatch(setPendingCountryChange(null));
      });
      return;
    }

    // Already on the target country page: apply city/pincode and continue here.
    const shouldRetryAddToCart = shouldRetryAddToCartAfterClear;
    await applyPendingLocationCookies();

    closeClearCartWithAnimation(() => {
      dispatch(setIsShowCartEmpty(false));
      dispatch(setIsClearCartDueToCountryChange(false));
      dispatch(setPendingCountryChange(null));
      dispatch(setSelectedDomesticLocationForCart({}));
      dispatch(setSelectedIntLocationForCart({}));

      if (shouldRetryAddToCart) {
        dispatch(setShouldRetryAddToCartAfterClear(true));
        return;
      }

      dispatch(setShouldRetryAddToCartAfterClear(false));
      dispatch(setIsShowMobileLocationLockAction(false));
      dispatch(setIsShowMobileLocationLockDrawerAction(false));
      dispatch(setIsShowDesktopLocationLockAction(false));
      // Body lock is reference-counted; location/preCheckout drawers release their own locks.
    });
  };

  /**
   * Back arrow / Change dismiss: slide drawer down, then clear state.
   * @returns {void}
   */
  const onHandleCloseClearCart = () => {
    trackClearCartPopupClosed();
    closeClearCartWithAnimation(() => {
      resetClearCartState({ clearLocationInput: true });
    });
  };

  /**
   * Drawer finished its own close animation (backdrop / swipe) — unmount immediately.
   * @returns {void}
   */
  const onHandleDrawerCloseAfterAnimation = () => {
    trackClearCartPopupClosed();
    resetClearCartState({ clearLocationInput: true });
  };

  /**
   * Change Location (same-catalog OOS/serviceability only):
   * apply pending location cookies and resume the confirm flow (does not clear cart).
   * @returns {Promise<void>}
   */
  const onHandleChangeLocation = async () => {
    trackClearCartClicked(clearCartConstants.CHANGE_LOCATION);
    await applyPendingLocationCookies();

    // Arm sync skip before synthetic Confirm so validation does not reopen ClearCart
    // before Redux skipCartValidationOnce re-renders subscribers.
    armSkipCartValidationOnce();
    dispatch(setSkipCartValidationOnce(true));

    closeClearCartWithAnimation(() => {
      dispatch(setIsShowCartEmpty(false));
      dispatch(setIsClearCartDueToCountryChange(false));
      dispatch(setPendingCountryChange(null));
      dispatch(setUnserviceableCartItems([]));
      dispatch(setSelectedDomesticLocationForCart({}));
      dispatch(setSelectedIntLocationForCart({}));

      setTimeout(() => {
        const locationLockContinueButton =
          document.getElementById("location-lock-continue-button") ||
          document.getElementById("location-lock-saved-address-confirm-button");
        if (locationLockContinueButton) {
          locationLockContinueButton.click();
        } else if (isMobile) {
          dispatch(setIsShowMobileLocationLockAction(false));
          dispatch(setIsShowMobileLocationLockDrawerAction(false));
        } else {
          dispatch(setIsShowDesktopLocationLockAction(false));
        }
      }, 0);
    });
  };

  const content = (
    <div className="flex w-full flex-col gap-4 rounded-[16px] bg-white-900 md:w-[500px] md:gap-0">
      <ClearCartHeader isMobile={isMobile} onClose={onHandleCloseClearCart} />

      <div className="flex w-full flex-col gap-[16px] p-16">
        <ClearCartWarning
          selectedLocationLabel={selectedLocationLabel}
          isDomestic={isDomesticLocation}
          isCountryChange={isCountryChange}
        />
        <div className="max-h-[45vh] overflow-auto scrollbar-thin md:rounded-t-8">
          <CartItemsList
            items={itemsToShow?.length > 0 ? itemsToShow : []}
            isMobile={isMobile}
            selectedCurrency={selectedCurrency}
            isCountryChange={isCountryChange}
          />
        </div>
      </div>

      <ClearCartFooter
        onChangeLocation={onHandleChangeLocation}
        onViewCart={onHandleViewCart}
        onChangeCountry={onHandleChangeCountry}
        isChangeCountry={isCountryChange}
      />
    </div>
  );
  if (itemsToShow?.length === 0) {
    return null;
  }
  if (isMobile) {
    return (
      <Portal rootId="fnpRootId" renderOnDidMount={true}>
        <Drawer
          position="bottom"
          isOpen={isDrawerOpen}
          onDrawerClose={onHandleDrawerCloseAfterAnimation}
          isHideCloseBtn={true}
          styles="py-0 px-0 max-h-[80vh] rounded-t-24"
          zIndex={1000000}
        >
          {content}
        </Drawer>
      </Portal>
    );
  }

  return (
    <PopOver
      isVisible={true}
      classes="overflow-hidden fixed z-10 inline-block rounded-2xl bg-white-900 text-sm text-black-900 shadow-sm transition-opacity duration-300 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
      isBackdrop={true}
      onClose={onHandleCloseClearCart}
    >
      {content}
    </PopOver>
  );
};

ClearCart.propTypes = {
  isMobile: PropTypes.bool.isRequired,
  selectedCurrency: PropTypes.object,
};

export default ClearCart;
